import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBeers } from '../hooks/useBeers';
import { Icon } from '../components/Icon';
import { BeerStatusBadge } from '../components/BeerStatusBadge';
import { BeerCardGrid } from '../components/BeerCardGrid';
import { BeerTable } from '../components/BeerTable';
import type { Beer } from '../api/types';
import '../styles/beers.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function useDebounce<T>(value: T, ms = 280): T {
  const [debounced, setDebounced] = useState(value);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setDebounced(value), ms);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [value, ms]);
  return debounced;
}

function useOutsideClick(ref: React.RefObject<HTMLElement | null>, cb: () => void) {
  React.useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) cb();
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [ref, cb]);
}

// ---------------------------------------------------------------------------
// Style filter dropdown
// ---------------------------------------------------------------------------

function StyleFilter({
  styles,
  selected,
  onChange,
}: {
  styles: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOutsideClick(ref, () => setOpen(false));

  const toggle = (style: string) => {
    const next = new Set(selected);
    next.has(style) ? next.delete(style) : next.add(style);
    onChange(next);
  };

  const label = selected.size === 0
    ? 'Style'
    : selected.size === 1
      ? [...selected][0]
      : `${selected.size} styles`;

  return (
    <div ref={ref} className="beers-style-filter">
      <button
        onClick={() => setOpen(o => !o)}
        className={`beers-style-filter-trigger${selected.size > 0 ? ' is-active' : ''}`}
      >
        <Icon name="filter" size={13} />
        {label}
        {selected.size > 0 && (
          <span
            role="button"
            aria-label="Clear style filter"
            title="Clear style filter"
            onClick={(e) => { e.stopPropagation(); onChange(new Set()); }}
            className="beers-style-filter-clear"
          >
            <Icon name="close" size={11} />
          </span>
        )}
      </button>

      {open && (
        <div className="beers-style-filter-menu">
          {styles.length === 0 && (
            <div className="beers-style-filter-empty">
              No styles available
            </div>
          )}
          {styles.map(s => (
            <label key={s} className={`beers-style-filter-option${selected.has(s) ? ' is-selected' : ''}`}>
              <input
                type="checkbox"
                checked={selected.has(s)}
                onChange={() => toggle(s)}
                className="beers-style-filter-checkbox"
              />
              {s}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ABV range inputs
// ---------------------------------------------------------------------------

function AbvRange({
  min, max,
  onChange,
}: {
  min: string; max: string;
  onChange: (min: string, max: string) => void;
}) {
  const filled = min !== '' || max !== '';
  return (
    <div className="beers-abv-range">
      <span className="uppercase-label beers-abv-label">ABV</span>
      <input
        type="number"
        min={0} max={20} step={0.1}
        value={min}
        onChange={(e) => onChange(e.target.value, max)}
        placeholder="0"
        className="beers-abv-input"
      />
      <span className="beers-abv-separator">-</span>
      <input
        type="number"
        min={0} max={20} step={0.1}
        value={max}
        onChange={(e) => onChange(min, e.target.value)}
        placeholder="20"
        className="beers-abv-input"
      />
      {filled && (
        <button
          onClick={() => onChange('', '')}
          aria-label="Clear ABV filter"
          title="Clear ABV filter"
          className="beers-abv-clear"
        >
          <Icon name="close" size={13} />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail modal
// ---------------------------------------------------------------------------

function BeerDetailModal({ beer, onClose }: { beer: Beer; onClose: () => void }) {
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      className="beers-modal-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="beers-modal-panel"
      >
        {/* Artwork */}
        {beer.artworkBlobUri && (
          <img
            src={beer.artworkBlobUri}
            alt={beer.name}
            className="beers-modal-artwork"
          />
        )}

        {/* Header */}
        <div className="beers-modal-header">
          <div>
            <div className="beers-modal-title-row">
              <span className="beers-modal-title">{beer.name}</span>
              <BeerStatusBadge status={beer.status} />
            </div>
            <div className="beers-modal-meta">
              {beer.style}{beer.abv != null ? ` · ${beer.abv}% ABV` : ''}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close beer details"
            title="Close beer details"
            className="beers-modal-close"
          >
            <Icon name="close" size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="beers-modal-body">
          {/* Key specs */}
          <div className="beers-modal-specs">
            {beer.abv != null && (
              <div>
                <div className="uppercase-label beers-modal-label">ABV</div>
                <div className="mono beers-modal-metric">{beer.abv}%</div>
              </div>
            )}
            {beer.priceKeg != null && (
              <div>
                <div className="uppercase-label beers-modal-label">Half-barrel</div>
                <div className="mono beers-modal-metric">${beer.priceKeg}</div>
              </div>
            )}
            {beer.priceSixtel != null && (
              <div>
                <div className="uppercase-label beers-modal-label">Sixtel</div>
                <div className="mono beers-modal-metric">${beer.priceSixtel}</div>
              </div>
            )}
          </div>

          {/* Menu description */}
          {beer.menuDescription && (
            <div>
              <div className="uppercase-label beers-modal-section-label">Menu headline</div>
              <p className="beers-modal-menu-copy">
                {beer.menuDescription}
              </p>
            </div>
          )}

          {/* Description */}
          {beer.description && (
            <div>
              <div className="uppercase-label beers-modal-section-label">What to tell guests</div>
              <p className="beers-modal-description-copy">
                {beer.description}
              </p>
            </div>
          )}

          {/* Tags */}
          {beer.tags.length > 0 && (
            <div>
              <div className="uppercase-label beers-modal-section-label">Tags</div>
              <div className="beers-modal-tags">
                {beer.tags.map(t => (
                  <span key={t} className="beers-modal-tag">{t}</span>
                ))}
              </div>
            </div>
          )}

          {/* Dates */}
          <div className="beers-modal-dates">
            Added {new Date(beer.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
            {beer.updatedAt !== beer.createdAt && (
              <> · Updated {new Date(beer.updatedAt).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}</>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status tabs
// ---------------------------------------------------------------------------

type StatusFilter = 'active' | 'pending' | 'retired' | 'all';

const STATUS_TABS: { k: StatusFilter; label: string }[] = [
  { k: 'active',  label: 'Active'  },
  { k: 'pending', label: 'Pending' },
  { k: 'retired', label: 'Retired' },
  { k: 'all',     label: 'All'     },
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function BeersPage(): React.ReactElement {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [search, setSearch] = useState('');
  const [selectedStyles, setSelectedStyles] = useState<Set<string>>(new Set());
  const [abvMin, setAbvMin] = useState('');
  const [abvMax, setAbvMax] = useState('');
  const [selectedBeer, setSelectedBeer] = useState<Beer | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>(() => {
    return (localStorage.getItem('beers-view-mode') as 'table' | 'cards') ?? 'table';
  });

  const handleViewMode = (mode: 'table' | 'cards') => {
    setViewMode(mode);
    localStorage.setItem('beers-view-mode', mode);
  };

  const debouncedSearch = useDebounce(search);

  const queryParams = useMemo(() => {
    const base = { limit: 200, ...(debouncedSearch ? { search: debouncedSearch } : {}) };
    if (statusFilter === 'all') return { ...base, includeRetired: true as const };
    return { ...base, status: statusFilter as string };
  }, [statusFilter, debouncedSearch]);

  const { data, isLoading, isError } = useBeers(queryParams);

  // All unique styles from current result set (for the style filter dropdown)
  const allStyles = useMemo(() => {
    const set = new Set<string>();
    for (const b of data?.items ?? []) if (b.style) set.add(b.style);
    return [...set].sort();
  }, [data]);

  // Client-side ABV + style filters applied on top of server-side results
  const beers = useMemo(() => {
    let items = data?.items ?? [];
    const minNum = abvMin !== '' ? parseFloat(abvMin) : null;
    const maxNum = abvMax !== '' ? parseFloat(abvMax) : null;
    if (selectedStyles.size > 0) {
      items = items.filter(b => selectedStyles.has(b.style));
    }
    if (minNum !== null && !isNaN(minNum)) {
      items = items.filter(b => b.abv != null && b.abv >= minNum);
    }
    if (maxNum !== null && !isNaN(maxNum)) {
      items = items.filter(b => b.abv != null && b.abv <= maxNum);
    }
    return items;
  }, [data, selectedStyles, abvMin, abvMax]);

  const hasActiveFilters = selectedStyles.size > 0 || abvMin !== '' || abvMax !== '';

  const clearAllFilters = () => {
    setSearch('');
    setSelectedStyles(new Set());
    setAbvMin('');
    setAbvMax('');
  };

  return (
    <>
      <div className="beers-page">

        {/* Header */}
        <div className="beers-header-wrap">
          <div className="beers-header-row">
            <div>
              <div className="uppercase-label beers-catalog-label">Catalog</div>
              <h1 className="beers-title">
                Beers
              </h1>
              <div className="beers-subtitle">
                {isLoading ? 'Loading…' : `${data?.total ?? 0} beers · ${beers.length} shown`}
              </div>
            </div>
            <button
              className="btn btn-primary beers-add-button"
              onClick={() => navigate('/beers/new')}
            >
              <Icon name="plus" size={14} /> Add Beer
            </button>
          </div>
        </div>

        {/* Search bar */}
        <div className="beers-search-wrap">
          <div className="beers-search-icon">
            <Icon name="search" size={16} />
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search beers by name or style…"
            className="beers-search-input"
          />
        </div>

        {/* Filter bar */}
        <div className="beers-filter-bar">
          {/* Status tabs — left */}
          <div className="beers-segmented">
            {STATUS_TABS.map(t => (
              <button
                key={t.k}
                onClick={() => setStatusFilter(t.k)}
                className={`beers-segmented-btn${statusFilter === t.k ? ' is-active' : ''}`}
              >{t.label}</button>
            ))}
          </div>

          {/* Style multi-select */}
          <StyleFilter
            styles={allStyles}
            selected={selectedStyles}
            onChange={setSelectedStyles}
          />

          {/* ABV range */}
          <AbvRange
            min={abvMin} max={abvMax}
            onChange={(min, max) => { setAbvMin(min); setAbvMax(max); }}
          />

          {/* Clear filters */}
          {(search || hasActiveFilters) && (
            <button
              onClick={clearAllFilters}
              className="beers-clear-button"
            >
              <Icon name="close" size={13} /> Clear
            </button>
          )}

          {/* Spacer */}
          <div className="beers-spacer" />

          {/* View toggle — right */}
          <div className="beers-segmented">
            {(['table', 'cards'] as const).map(mode => (
              <button
                key={mode}
                onClick={() => handleViewMode(mode)}
                className={`beers-segmented-btn beers-segmented-btn-with-icon${viewMode === mode ? ' is-active' : ''}`}
              >
                {mode === 'table' ? (
                  <>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                      <line x1="3" y1="9" x2="21" y2="9"/>
                      <line x1="3" y1="15" x2="21" y2="15"/>
                      <line x1="9" y1="3" x2="9" y2="21"/>
                    </svg>
                    Table
                  </>
                ) : (
                  <>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="7" height="7" rx="1"/>
                      <rect x="14" y="3" width="7" height="7" rx="1"/>
                      <rect x="3" y="14" width="7" height="7" rx="1"/>
                      <rect x="14" y="14" width="7" height="7" rx="1"/>
                    </svg>
                    Cards
                  </>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Error */}
        {isError && (
          <div className="beers-error-banner">
            <Icon name="alert" size={18} />
            Could not load beers. Check your connection and try again.
          </div>
        )}

        {/* Loading skeletons */}
        {isLoading && (
          <div className="beers-loading-grid">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="beers-loading-skeleton" />
            ))}
          </div>
        )}

        {/* View */}
        {!isLoading && viewMode === 'table' && (
          <BeerTable beers={beers} onBeerClick={setSelectedBeer} />
        )}
        {!isLoading && viewMode === 'cards' && (
          <BeerCardGrid beers={beers} onBeerClick={setSelectedBeer} />
        )}

        {/* Empty state */}
        {!isLoading && !isError && beers.length === 0 && (
          <div className="beers-empty-state">
            {search || hasActiveFilters
              ? 'No beers match the current filters.'
              : `No ${statusFilter === 'all' ? '' : statusFilter + ' '}beers in the catalog yet.`}
          </div>
        )}

      </div>

      {/* Detail modal */}
      {selectedBeer && (
        <BeerDetailModal beer={selectedBeer} onClose={() => setSelectedBeer(null)} />
      )}
    </>
  );
}
